import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMenu, FiX, FiChevronRight, FiAlertOctagon } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Study Materials', path: '/study-materials' },
        { name: 'Schedules', path: '#timetable' },
        { name: 'About us', path: '/about' },
        { name: 'Gallery', path: '/gallery' }
    ];

    const handleClick = (e, path) => {
        if (path.startsWith('#')) {
            e.preventDefault();
            const element = document.getElementById(path.substring(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            } else {
                // If not on home page, navigate to home then scroll
                window.location.href = '/#/' + path;
            }
            setIsOpen(false);
        }
    };

    return (
        <nav className={`fixed top-0 left-0 w-full z-[1000] transition-all duration-500 flex justify-center px-4 md:px-8 pt-6 ${scrolled ? 'translate-y-[-10px]' : 'translate-y-0'
            }`}>
            <motion.div
                initial={false}
                animate={{
                    width: scrolled ? '90%' : '100%',
                    maxWidth: '1200px',
                    backgroundColor: scrolled ? 'rgba(10, 10, 10, 0.8)' : 'rgba(10, 10, 10, 0.4)',
                    backdropFilter: 'blur(20px)',
                    padding: scrolled ? '12px 24px' : '16px 32px',
                }}
                className="relative flex justify-between items-center rounded-full border border-white/10 shadow-2xl overflow-hidden"
            >
                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 pointer-events-none"></div>

                <Link to="/" className="relative z-10 flex items-center gap-2 group">
                    <div className="text-lg md:text-xl font-black text-white tracking-tight">
                        MANOJ <span className="text-blue-500">SAMPATH</span>
                    </div>
                </Link>

                {/* Desktop Links - Centered */}
                <div className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
                    {navLinks.map((link) => (
                        link.path.startsWith('#') ? (
                            <a
                                key={link.name}
                                href={link.path}
                                onClick={(e) => handleClick(e, link.path)}
                                className="text-[12px] font-bold uppercase tracking-widest text-neutral-400 hover:text-white transition-all relative group cursor-pointer"
                            >
                                {link.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-[3px] bg-blue-500 transition-all duration-300 group-hover:w-full"></span>
                            </a>
                        ) : (
                            <Link
                                key={link.name}
                                to={link.path}
                                className="text-[14px] font-bold uppercase tracking-widest text-neutral-400 hover:text-white transition-all relative group"
                            >
                                {link.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-[3px] bg-blue-500 transition-all duration-300 group-hover:w-full"></span>
                            </Link>
                        )
                    ))}
                </div>

                {/* Desktop Actions */}
                <div className="hidden lg:flex items-center gap-4 relative z-10">
                    <Link to="/feedback">
                        <button className="text-[11px] font-bold uppercase tracking-widest text-white/70 hover:text-white px-4 py-2 transition-colors">
                            <FiAlertOctagon />
                        </button>
                    </Link>
                    <Link to="/login">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-white text-black px-6 py-2.5 rounded-full font-bold text-[11px] uppercase tracking-widest transition-all hover:bg-neutral-200"
                        >
                            Sign Up
                        </motion.button>
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="lg:hidden w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white text-lg transition-all active:scale-90"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? <FiX /> : <FiMenu />}
                </button>
            </motion.div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="fixed inset-4 top-24 pt-10 h-[80vh] bottom-4 bg-[#0a0a0a] backdrop-blur-30xl z-[999] lg:hidden rounded-[2.5rem] border border-white/10 flex flex-col p-10 shadow-2xl"
                    >
                        <div className="flex flex-col gap-6">
                            {navLinks.map((link, i) => (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    key={link.name}
                                >
                                    {link.path.startsWith('#') ? (
                                        <a
                                            href={link.path}
                                            className="text-3xl font-black text-white tracking-tight flex items-center justify-between group"
                                            onClick={(e) => handleClick(e, link.path)}
                                        >
                                            {link.name}
                                            <FiChevronRight className="text-xl text-blue-500" />
                                        </a>
                                    ) : (
                                        <Link
                                            to={link.path}
                                            className="text-3xl font-black text-white tracking-tight flex items-center justify-between group"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {link.name}
                                            <FiChevronRight className="text-xl text-blue-500" />
                                        </Link>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-auto space-y-4">
                            <Link to="/login" onClick={() => setIsOpen(false)} className="block">
                                <button className="w-full bg-white text-black py-5 rounded-full font-bold text-sm uppercase tracking-widest">
                                    Secure Login
                                </button>
                            </Link>
                            <p className="text-center text-neutral-600 text-[10px] font-bold uppercase tracking-widest">
                                &copy; {new Date().getFullYear()} Manoj Sampath
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
